import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { PageHeader } from '@platform/ui-kit';
import { api, type AirPlatform } from '../../../api';

/**
 * Strategic Intelligence → AI Platforms Catalog.
 *
 * Ported from the source's `views/platforms/index.ejs`. The card's CONTENT is the
 * source's, field for field — name, category, the positioning truncated at 110
 * characters, and the "Cost:" line pulled from the first `profile.assessment` entry whose
 * `area` starts with "Cost". The chrome is the platform's MUI, because the source is
 * server-rendered Materialize and there is nothing to copy.
 *
 * The generated badge is the source's algorithm reproduced exactly: initials from the
 * platform code, and a hue derived from a rolling hash of the name, so a platform keeps
 * the same colour here as it has there.
 */

/** The source's `platBadge()` — same hash, same hues, so colours match the source. */
function platformBadge(p: AirPlatform): { initials: string; c1: string; c2: string } {
  const base = String(p.platform_code || p.name || '?').replace(/\(.*/, '').trim();
  const initials =
    base.length <= 4
      ? base.toUpperCase()
      : base
          .split(/[\s/+-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0])
          .join('')
          .toUpperCase();
  const s = String(p.name || base);
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return {
    initials: initials || '?',
    c1: `hsl(${h},52%,42%)`,
    c2: `hsl(${(h + 28) % 360},58%,52%)`,
  };
}

/** The source reads the first assessment area beginning "Cost" and shows its label. */
function costLabel(p: AirPlatform): string | null {
  const assessment = p.profile?.assessment;
  if (!Array.isArray(assessment)) return null;
  const c = assessment.find((a) => typeof a?.area === 'string' && a.area.indexOf('Cost') === 0);
  return c?.label ?? null;
}

export function AiPlatformsCatalog() {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState<AirPlatform[]>([]);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.aiReadiness
      .getPlatforms()
      .then((d) => { if (active) setPlatforms(d.platforms); })
      .catch((e) => { if (active) setError(e?.message || 'Could not load the catalog.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const subtitle = useMemo(
    () =>
      `${platforms.length} AI platforms in the catalog. Each profile is generic — what the platform is, ` +
      `how it runs, and a practical at-a-glance read. Scoring against opportunities happens later, in the Fit tool.`,
    [platforms.length],
  );

  return (
    <>
      <PageHeader title="AI Platforms" description={loading ? '' : subtitle} />

      {error ? (
        <Typography sx={{ fontSize: 13, color: 'error.main', mb: 2 }}>{error}</Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' },
        }}
      >
        {platforms.map((p) => {
          const badge = platformBadge(p);
          const cost = costLabel(p);
          // `profile.positioning` ONLY, as the source does. The catalogue query — copied
          // from the source — does not select the `positioning` COLUMN, so falling back
          // to it would have been dead code implying behaviour that cannot occur.
          const positioning = p.profile?.positioning ?? '';
          return (
            <Card key={p.id} sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() =>
                  navigate(`/ai_readiness/strategic-intelligence/ai-platforms-catalog/${p.id}`)
                }
                sx={{ height: '100%', alignItems: 'stretch' }}
              >
                <CardContent sx={{ height: '100%', p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', mb: '2px' }}>
                    <Box
                      aria-hidden
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        width: 38,
                        height: 38,
                        borderRadius: '9px',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        background: `linear-gradient(135deg, ${badge.c1}, ${badge.c2})`,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                      }}
                    >
                      {badge.initials}
                    </Box>
                    <Typography sx={{ fontSize: '1.05rem', color: '#1565c0', fontWeight: 500 }}>
                      {p.name}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: 14, color: '#616161', m: '4px 0' }}>
                    {p.category || ''}
                  </Typography>

                  {positioning ? (
                    <Typography sx={{ fontSize: '0.88rem', color: '#9e9e9e', m: '6px 0 0' }}>
                      {positioning.slice(0, 110)}
                    </Typography>
                  ) : null}

                  {cost ? (
                    <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e', m: '8px 0 0' }}>
                      Cost: <strong>{cost}</strong>
                    </Typography>
                  ) : null}
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </>
  );
}
